// src/app/core/toast/toast.service.ts
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ToastItem, ToastOptions, ToastPosition } from './toast.types';
import { TOAST_CONFIG, ToastConfig } from './toast.tokens';

let seed = 0;
function uid() { return `t_${Date.now().toString(36)}_${(seed++).toString(36)}`; }

@Injectable({ providedIn: 'root' })
export class ToastService {
    private _toasts$ = new BehaviorSubject<ToastItem[]>([]);
    readonly toasts$ = this._toasts$.asObservable();

    constructor(@Inject(TOAST_CONFIG) private cfg: ToastConfig) {}

    show(opts: ToastOptions): string {
        const id = opts.id ?? uid();
        const item: ToastItem = {
            id,
            createdAt: Date.now(),
            title: opts.title,
            message: opts.message,
            type: opts.type ?? 'success',
            position: opts.position ?? this.cfg.defaultPosition,
            variant: (opts as any).variant ?? this.cfg.defaultVariant, // falls ihr Varianten nutzt
            duration: opts.duration ?? this.cfg.defaultDuration,
            dismissible: opts.dismissible ?? true,
            icon: opts.icon,
            data: opts.data
        };
        const list = this._toasts$.value.slice();
        list.push(item);
        this._toasts$.next(list);
        if (item.duration && item.duration > 0) {
            setTimeout(() => this.dismiss(id), item.duration);
        }
        return id;
    }

    // Convenience:
    success(message: string, title?: string, duration?: number, position?: ToastPosition): string;
    success(message: string, opts?: Partial<ToastOptions>): string;
    success(message: string, a?: any, b?: number, c?: ToastPosition): string {
        if (typeof a === 'object' && a !== null) {
            return this.show({ ...a, message, type: 'success', icon: 'check_circle' } as ToastOptions);
        }
        return this.show({ message, title: a as string, type: 'success', duration: b, position: c, icon: 'check_circle' });
    }

    info(message: string, title?: string, duration?: number, position?: ToastPosition): string;
    info(message: string, opts?: Partial<ToastOptions>): string;
    info(message: string, a?: any, b?: number, c?: ToastPosition): string {
        if (typeof a === 'object' && a !== null) {
            return this.show({ ...a, message, type: 'info', icon: 'info' } as ToastOptions);
        }
        return this.show({ message, title: a as string, type: 'info', duration: b, position: c, icon: 'info' });
    }

    warn(message: string, title?: string, duration?: number, position?: ToastPosition): string;
    warn(message: string, opts?: Partial<ToastOptions>): string;
    warn(message: string, a?: any, b?: number, c?: ToastPosition): string {
        if (typeof a === 'object' && a !== null) {
            return this.show({ ...a, message, type: 'warn', icon: 'warning' } as ToastOptions);
        }
        return this.show({ message, title: a as string, type: 'warn', duration: b, position: c, icon: 'warning' });
    }

    error(message: string, title?: string, duration?: number, position?: ToastPosition): string;
    error(message: string, opts?: Partial<ToastOptions>): string;
    error(message: string, a?: any, b?: number, c?: ToastPosition): string {
        if (typeof a === 'object' && a !== null) {
            return this.show({ ...a, message, type: 'error', icon: 'error' } as ToastOptions);
        }
        return this.show({ message, title: a as string, type: 'error', duration: b, position: c, icon: 'error' });
    }

    /** <-- Diese Methode wird von der Komponente aufgerufen */
    dismiss(id: string) {
        const list = this._toasts$.value.filter(t => t.id !== id);
        this._toasts$.next(list);
    }

    clear() {
        this._toasts$.next([]);
    }
}
